// =============================================================================
// GenericElementPanel — Panel lateral para elementos de planta (zanjas, inversores, etc.)
// Replica la filosofía de TrackerDetailPanel pero para cualquier capa con pasos WBS.
// =============================================================================

import { useState, useEffect, useRef } from 'react'
import { X } from 'lucide-react'
import type { StepConfig } from '@/lib/layerRegistry'
import { TRACKER_STATUS_META, type TrackerStatus } from '@/types/plant'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface GenericElementPanelProps {
  layerKey:        string               // 'zanjas_bt', 'inversores', etc.
  layerLabel:      string               // 'Zanjas BT', 'Inversores', etc.
  featureId:       string               // ID del elemento (lineId, handle, parcela…)
  steps:           readonly StepConfig[]
  initialStepCode?: string             // paso activo cargado de localStorage
  layerColor:      string               // color del layer para el icono
  onClose:         () => void
  onSave:          (layerKey: string, featureId: string, stepCode: string) => void
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function GenericElementPanel({
  layerKey, layerLabel, featureId, steps, initialStepCode, layerColor, onClose, onSave,
}: GenericElementPanelProps) {
  const [activeStepCode, setActiveStepCode] = useState<string | undefined>(initialStepCode)
  const [saved,          setSaved]          = useState(false)
  const prevFeatureId = useRef(featureId)

  // Reset cuando cambia el elemento seleccionado
  useEffect(() => {
    if (prevFeatureId.current !== featureId) {
      prevFeatureId.current = featureId
      setActiveStepCode(initialStepCode)
      setSaved(false)
    }
  }, [featureId, initialStepCode])

  // Estado derivado a partir del paso activo
  const derivedStatus: TrackerStatus = !activeStepCode
    ? 'NO_INICIADA'
    : steps[steps.length - 1]?.code === activeStepCode
      ? 'FINALIZADA'
      : 'EN_CURSO'

  const statusMeta  = TRACKER_STATUS_META[derivedStatus]
  const activeIdx   = activeStepCode ? steps.findIndex(s => s.code === activeStepCode) : -1
  const progressPct = activeIdx === -1 ? 0 : Math.round(((activeIdx + 1) / steps.length) * 100)

  const handleSave = () => {
    if (!activeStepCode) return
    onSave(layerKey, featureId, activeStepCode)
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  return (
    <div className="flex flex-col h-full text-sm">

      {/* ── Cabecera ─────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between p-4 border-b border-slate-700 shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: layerColor }} />
          <span className="text-[10px] text-slate-500 uppercase tracking-wider shrink-0">{layerLabel}</span>
          <span className="font-mono text-xs text-slate-300 truncate">{featureId}</span>
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-colors shrink-0 ml-2"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">

        {/* ── Estado derivado + progreso ──────────────────────────────────── */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: statusMeta.color }} />
              <span className="text-xs text-slate-400">{statusMeta.label}</span>
            </div>
            <span className="text-base font-bold text-white">{progressPct}%</span>
          </div>
          <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width:      `${progressPct}%`,
                backgroundColor: statusMeta.color,
                boxShadow:  progressPct > 0 ? `0 0 6px ${statusMeta.glowColor}` : undefined,
              }}
            />
          </div>
        </div>

        {/* ── Lista de pasos WBS ──────────────────────────────────────────── */}
        <div>
          <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Pasos WBS</p>
          <div className="flex flex-col gap-1">
            {steps.map((step, idx) => {
              const isActive  = activeStepCode === step.code
              const isDone    = activeIdx !== -1 && idx <= activeIdx
              return (
                <button
                  key={step.code}
                  onClick={() => { setActiveStepCode(step.code); setSaved(false) }}
                  className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-[11px] font-medium border transition-all duration-150 text-left"
                  style={
                    isActive
                      ? { backgroundColor: `${step.color}25`, borderColor: step.color, color: step.glowColor, boxShadow: `0 0 8px ${step.color}30` }
                      : isDone
                      ? { backgroundColor: '#16A34A20', borderColor: '#16A34A60', color: '#4ADE80' }
                      : { backgroundColor: 'transparent', borderColor: '#334155', color: '#64748B' }
                  }
                >
                  <span className="text-[9px] opacity-60 w-7 shrink-0 font-mono">{step.code}</span>
                  <span className="flex-1 truncate">{step.label}</span>
                  {isDone && !isActive && <span className="text-green-400 text-[10px]">✓</span>}
                  {isActive && (
                    <span className="text-[9px] opacity-60 shrink-0">
                      {Math.round(step.weight * 100)}%
                    </span>
                  )}
                </button>
              )
            })}
          </div>

          {/* Atajos de estado rápido */}
          <div className="flex gap-1.5 mt-2">
            {(['NO_INICIADA', 'BLOQUEADA'] as TrackerStatus[]).map((s) => {
              const m = TRACKER_STATUS_META[s]
              const isSelected = s === 'NO_INICIADA' ? !activeStepCode : false
              return (
                <button
                  key={s}
                  onClick={() => {
                    if (s === 'NO_INICIADA') setActiveStepCode(undefined)
                    setSaved(false)
                  }}
                  className={`flex-1 px-2 py-1 rounded-lg text-[10px] font-medium border transition-all duration-150
                    ${isSelected ? 'text-white' : 'border-slate-700 text-slate-500 hover:border-slate-500 hover:text-slate-300'}`}
                  style={isSelected ? { backgroundColor: `${m.color}25`, borderColor: m.color, color: m.glowColor } : undefined}
                >
                  {m.label}
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {/* ── Botón guardar ────────────────────────────────────────────────── */}
      <div className="p-4 border-t border-slate-700 shrink-0">
        <button
          onClick={handleSave}
          disabled={!activeStepCode && derivedStatus !== 'NO_INICIADA'}
          className={`w-full py-2.5 rounded-xl font-semibold text-sm transition-all duration-200
            ${saved
              ? 'bg-green-600 text-white shadow-[0_0_20px_#22C55E60]'
              : 'bg-amber-500 hover:bg-amber-400 text-slate-900 shadow-[0_0_20px_#F59E0B40] disabled:opacity-40 disabled:cursor-not-allowed'
            }`}
        >
          {saved ? '✓ Guardado' : 'Guardar estado'}
        </button>
      </div>
    </div>
  )
}
